'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import {
  Group,
  Text,
  Title,
  Stack,
  Card,
  Divider,
  Button,
  Container,
  Tabs,
  Accordion,
  Space,
  Badge,
  Select,
  Loader,
} from '@mantine/core';
import { IconLayoutGrid, IconFileText, IconUsers } from '@tabler/icons-react';
import styles from './SiteOverviewPanel.module.scss';
import FuturisticStatCircle from './FuturisticStatCircle/FuturisticStatCircle';
import { usePathname, useSearchParams, useRouter, useParams } from 'next/navigation';
import { Locale } from '@/i18n-config';
import { primaryColor, secondaryColor } from '@/src/styles/colors';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import AdminShortcuts from '../Admin/AdminShortcuts/AdminShortcuts';
import FullScreenImageHalf from '../SliceMachine/FullScreenImageHalf/FullScreenImageHalf';

interface OverviewItem {
  id: string;
  title: string;
  description: string | { [key in Locale]: string };
  link: string;
  imageUrl: string;
}

export interface OverviewClient {
  name: string;
  email: string;
  phone: string;
  _id: string;
}

interface SiteOverviewPanelProps {
  articles: {
    count: number;
    recent: OverviewItem[];
  };
  products: {
    count: number;
    recent: OverviewItem[];
  };
  tickets: {
    count: number;
    recent: OverviewItem[];
  };
  quotes: {
    count: number;
    recent: OverviewItem[];
  };
  invoices: {
    count: number;
    recent: OverviewItem[];
  };
  clients?: OverviewClient[];
  models?: any;
  services?: any;
}

type TicketLite = {
  _id: string;
  formData?: {
    clientName?: string;
    name?: string;
    email?: string;
    phone?: string;
    modelBrand?: string;
    modelName?: string;
    reference?: string;
  };
  createdAt?: string;
};

const ListWidget = ({
  title,
  icon,
  items,
  color,
  link,
}: {
  title: string;
  icon: string;
  items: OverviewItem[];
  color?: string;
  link: string;
}) => {
  const pathname = usePathname();
  const locale = pathname.split('/')[1] as Locale;

  return (
    <Card className={styles.listCard} radius="lg" padding="md">
      <Group mb="xs" justify="space-between">
        <Group>
          <img src={icon} alt={title} style={{ width: 20, height: 20 }} />
          <Title order={4} className={styles.listTitle}>
            {title}
          </Title>
        </Group>
        <Link href={`/${link}`} passHref>
          <Button size="xs" variant="outline" color={primaryColor}>
            Voir tout
          </Button>
        </Link>
      </Group>
      <Divider mb="sm" />
      <Stack gap="sm">
        {items.length === 0 ? (
          <Text size="sm" c="dimmed">
            Aucun élément
          </Text>
        ) : (
          items.slice(0, 4).map((item) => (
            <Group key={item.id} className={styles.itemLine} align="stretch">
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Link href={item.link} className={styles.itemLink}>
                  <Text size="md" fw={500} c={primaryColor} mb="lg">
                    {item.title}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {typeof item.description === 'object'
                      ? item.description[locale] || Object.values(item.description)[0]
                      : item.description}
                  </Text>
                </Link>
              </div>
              <div
                style={{
                  width: 100,
                  height: '100px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
                className={styles.imageWidget}
              >
                <img
                  src={item.imageUrl || '/default-image.jpg'}
                  alt={item.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </div>
            </Group>
          ))
        )}
      </Stack>
    </Card>
  );
};

const SiteOverviewPanel: React.FC<SiteOverviewPanelProps> = ({
  articles,
  products,
  quotes,
  tickets,
  invoices,
}) => {
  const dictionary = useDictionary();
  const searchParams = useSearchParams();
  const { lang } = useParams() as { lang: string };
  const router = useRouter();
  const pathname = usePathname();

  const initialTab = searchParams.get('tab') ?? 'admin';
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [dictionaryData, setDictionaryData] = useState<any>({});
  const [summaries, setSummaries] = useState<Record<string, any[]>>({});

  // ---------- Ticket selector state ----------
  const [ticketSearch, setTicketSearch] = useState('');
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketOptions, setTicketOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  const fetchTickets = async (query = '') => {
    try {
      setTicketLoading(true);

      // ✅ CHANGE THIS ENDPOINT if your tickets search endpoint differs
      // Expected: { tickets: TicketLite[] }
      const res = await fetch(
        `/api/accounting-manager/tickets?query=${encodeURIComponent(query)}&limit=20`
      );
      const json = await res.json();
      const list: TicketLite[] = json.tickets || [];

      const opts = list.map((t) => {
        const fd = t.formData || {};
        const client = fd.clientName || fd.name || 'Client';
        const model = [fd.modelBrand, fd.modelName].filter(Boolean).join(' ');
        const contact = [fd.email, fd.phone].filter(Boolean).join(' • ');
        const ref = fd.reference ? `#${fd.reference}` : t._id.slice(-6);
        const label = [ref, client, model, contact].filter(Boolean).join(' — ');
        return { value: t._id, label };
      });

      setTicketOptions(opts);
    } catch (e) {
      console.warn('Erreur chargement tickets', e);
      setTicketOptions([]);
    } finally {
      setTicketLoading(false);
    }
  };

  useEffect(() => {
    // initial load for selector
    fetchTickets('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Existing effects ----------
  useEffect(() => {
    (async () => {
      const data = dictionary.pages.admin.quickLinks.data;
      setDictionaryData(data);

      const loadedSummaries: Record<string, any[]> = {};
      await Promise.all(
        Object.keys(data).map(async (slug) => {
          try {
            const res = await fetch(`/api/${lang}/formdata/${slug}/summary`);
            const json = await res.json();
            loadedSummaries[slug] = json[slug] || [];
          } catch (e) {
            console.warn(`Erreur lors du chargement de formdata/${slug}/summary`, e);
          }
        })
      );

      setSummaries(loadedSummaries);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const paramTab = searchParams.get('tab');
    if (paramTab && paramTab !== activeTab) {
      setActiveTab(paramTab);
    }
  }, [searchParams, activeTab]);

  const handleTabChange = (value: string | null) => {
    if (!value) return;
    setActiveTab(value);
    const newParams = new URLSearchParams(Array.from(searchParams.entries()));
    newParams.set('tab', value);
    router.push(`${pathname}?${newParams.toString()}`, { scroll: false });
  };

  const goCreateQuoteFromTicket = () => {
    if (!selectedTicketId) return;
    router.push(`/admin/quotes/new/ticket/${selectedTicketId}`);
  };

  const goCreateInvoiceFromTicket = () => {
    if (!selectedTicketId) return;
    router.push(`/admin/invoices/new/ticket/${selectedTicketId}`);
  };

  return (
    <div className={styles.siteOverviewPanel}>
      <div className={styles.container}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="default" classNames={{ list: styles.tabsList }}>
          <Tabs.List grow pos="sticky">
            <Tabs.Tab value="content" ta="center">
              <IconFileText size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} color={primaryColor} />
              Contenu
              <Text c="dimmed" size="sm">
                Soyez plus visibles sur le net
              </Text>
            </Tabs.Tab>

            <Tabs.Tab value="admin" ta="center">
              <IconLayoutGrid size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} color={primaryColor} />
              Administratif
              <Text c="dimmed" size="sm">
                Simplifiez votre comptabilité et administratif
              </Text>
            </Tabs.Tab>

            <Tabs.Tab value="data" ta="center">
              <IconUsers size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} color={primaryColor} />
              Données
              <Text c="dimmed" size="sm">
                Automatisez les tâches répétitives
              </Text>
            </Tabs.Tab>
          </Tabs.List>

          {/* CONTENT */}
          <Tabs.Panel value="content">
            <FullScreenImageHalf
              data={{
                src: 'https://ik.imagekit.io/r5qpfvs48/articles/image_1748359649517_R0axlstEC.webp?updatedAt=1748359650381',
                alt: 'Soyez visibles sur internet',
                title: 'Soyez visibles sur internet',
                subtitle:
                  'Augmentez votre visibilité en publiant des artiles sur des sujets niches et en affichant vos produits sur le site.',
                absolute: false,
                bgMask: true,
              }}
            />
            <Container>
              <Group mt="md" justify="space-between">
                <AdminShortcuts iconItems={dictionary.pages.admin.quickLinks.content} />
                <Stack>
                  <Button color={primaryColor} component={Link} href="/admin/articles">
                    Créer un article
                  </Button>
                  <Button color={primaryColor} component={Link} href="/admin/products">
                    Créer un produit
                  </Button>
                </Stack>
              </Group>
              <Divider my="xl" />
              <Title order={2} my="xl">
                Chiffres clefs
              </Title>
              <Group mt="md" justify="space-between">
                <FuturisticStatCircle value={products.count ?? '-'} label="Produits" />
                <FuturisticStatCircle value={articles.count ?? '-'} label="Articles" />
              </Group>
              <Divider my="xl" />
              <Title order={2} my="xl">
                Derniers ajouts
              </Title>
              <Tabs defaultValue="articles">
                <Tabs.List grow>
                  <Tabs.Tab value="articles" leftSection={<img src="https://img.icons8.com/isometric-line/50/web-design.png" width={16} alt="Articles" />}>
                    Articles
                  </Tabs.Tab>
                  <Tabs.Tab value="products" leftSection={<img src="https://img.icons8.com/isometric-line/50/shopping-cart.png" width={16} alt="Produits" />}>
                    Produits
                  </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="articles" pt="sm">
                  <ListWidget title="Articles récents" icon="https://img.icons8.com/isometric-line/50/web-design.png" items={articles.recent} link="articles" />
                </Tabs.Panel>
                <Tabs.Panel value="products" pt="sm">
                  <ListWidget title="Produits récents" icon="https://img.icons8.com/isometric-line/50/shopping-cart.png" items={products.recent} link="products" />
                </Tabs.Panel>
              </Tabs>
            </Container>
          </Tabs.Panel>

          {/* ADMIN */}
          <Tabs.Panel value="admin">
            <FullScreenImageHalf
              data={{
                src: 'https://ik.imagekit.io/r5qpfvs48/articles/image_1748359649517_R0axlstEC.webp?updatedAt=1748359650381',
                alt: 'Gérez vos Demandes, Devis et Factures',
                title: 'Gérez vos Demandes, Devis et Factures',
                subtitle:
                  'Gagnez en efficacité en regroupant sur votre site la gestion de vos demandes, devis et factures. Traçabilité et Harmonisation.',
                absolute: false,
                bgMask: true,
              }}
            />

            <Container>
              <Group mt="md" justify="space-between" align="flex-start">
                <AdminShortcuts iconItems={dictionary.pages.admin.quickLinks.admin} />

                {/* ✅ One create ticket + ticket selector + actions */}
                <Stack style={{ minWidth: 360, width: "100%" }}>
                  <Button color={secondaryColor} component={Link} href="/admin/tickets/new">
                    Créer un Dépôt
                  </Button>
                  <Button color={primaryColor} component={Link} href="/admin/invoices/new">
                    Créer une Facture Express
                  </Button>

                  <Select
                    label="Rechercher un ticket"
                    placeholder="Tapez un nom, email, téléphone, modèle..."
                    data={ticketOptions}
                    searchable
                    value={selectedTicketId}
                    searchValue={ticketSearch}
                    onSearchChange={(v) => {
                      setTicketSearch(v);
                      fetchTickets(v);
                    }}
                    onChange={(value) => setSelectedTicketId(value)}
                    rightSection={ticketLoading ? <Loader size="xs" /> : null}
                    nothingFoundMessage={ticketLoading ? 'Chargement...' : 'Aucun ticket trouvé'}
                    clearable
                  />

                  <Group grow>
                    <Button
                      color={secondaryColor}
                      variant="outline"
                      disabled={!selectedTicketId}
                      onClick={goCreateQuoteFromTicket}
                    >
                      Créer un Devis
                    </Button>

                    <Button
                      color={secondaryColor}
                      disabled={!selectedTicketId}
                      onClick={goCreateInvoiceFromTicket}
                    >
                      Créer une Facture
                    </Button>
                  </Group>

                  {!selectedTicketId && (
                    <Text size="xs" c="dimmed">
                      Sélectionnez d’abord un ticket pour créer un devis ou une facture.
                    </Text>
                  )}
                </Stack>
              </Group>

              <Divider my="xl" />
              <Title order={2} my="xl">
                Chiffres clefs
              </Title>
              <Group mt="md" justify="space-between">
                <FuturisticStatCircle value={tickets.count ?? '-'} label="Dépôts" />
                <FuturisticStatCircle value={quotes.count ?? '-'} label="Devis" />
                <FuturisticStatCircle value={invoices.count ?? '-'} label="Factures" />
              </Group>

              <Divider my="xl" />
              <Title order={2} my="xl">
                Derniers ajouts
              </Title>
              <Tabs defaultValue="tickets">
                <Tabs.List grow>
                  <Tabs.Tab value="tickets" leftSection={<img src="https://img.icons8.com/isometric-line/50/web-design.png" width={16} alt="Tickets" />}>
                    Tickets
                  </Tabs.Tab>
                  <Tabs.Tab value="quotes" leftSection={<img src="https://img.icons8.com/isometric-line/50/money-bag.png" width={16} alt="Devis" />}>
                    Devis
                  </Tabs.Tab>
                  <Tabs.Tab value="invoices" leftSection={<img src="https://img.icons8.com/isometric-line/50/money-bag.png" width={16} alt="Factures" />}>
                    Factures
                  </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="tickets" pt="sm">
                  <ListWidget title="Tickets récents" icon="https://img.icons8.com/isometric-line/50/web-design.png" items={tickets.recent} link="admin/tickets" />
                </Tabs.Panel>
                <Tabs.Panel value="quotes" pt="sm">
                  <ListWidget title="Devis récents" icon="https://img.icons8.com/isometric-line/50/money-bag.png" items={quotes.recent} link="admin/quotes" />
                </Tabs.Panel>
                <Tabs.Panel value="invoices" pt="sm">
                  <ListWidget title="Factures récentes" icon="https://img.icons8.com/isometric-line/50/money-bag.png" items={invoices.recent} link="admin/invoices" />
                </Tabs.Panel>
              </Tabs>
            </Container>
          </Tabs.Panel>

          {/* DATA */}
          <Tabs.Panel value="data">
            <FullScreenImageHalf
              data={{
                src: 'https://ik.imagekit.io/r5qpfvs48/articles/image_1748359649517_R0axlstEC.webp?updatedAt=1748359650381',
                alt: 'Créez vos bases de donnée pour automatiser vos formulaires',
                title: 'Créez vos bases de données',
                subtitle:
                  "Facilitez l'entrée d'informations et évitez les taches répétitives en enregistrant les informations récurrentes.",
                absolute: false,
                bgMask: true,
              }}
            />
            <Container>
              <Title order={2} my="xl">
                Toutes vos bases de données
              </Title>

              {Object.entries(dictionaryData).map(([slug, config]) => {
                const typedConfig = config as {
                  label: string;
                  collection: string;
                  fields: string[];
                };

                return (
                  <div key={slug}>
                    <Divider mt="mb" mb="sm" />
                    <Group justify="space-between">
                      <Title order={3}>{typedConfig.label}</Title>
                      <Button
                        component={Link}
                        href={`/admin/data/${typedConfig.collection.toLowerCase()}`}
                        color={primaryColor}
                        variant="outline"
                      >
                        Gérer les {typedConfig.label}
                      </Button>
                    </Group>

                    <Accordion variant="filled">
                      <Accordion.Item value={`${slug}-recents`}>
                        <Accordion.Control c="dimmed">
                          Afficher les 10 derniers {typedConfig.label.toLowerCase()} enregistrés
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Stack>
                            {(summaries[slug] || []).map((item) => (
                              <Card key={item._id} withBorder radius="md" shadow="sm">
                                <Group justify="space-between">
                                  <div>
                                    {typedConfig.fields.map((field: string) =>
                                      item[field] ? (
                                        field === 'price' ? (
                                          <Badge key={field} color={primaryColor}>
                                            {item[field]} €
                                          </Badge>
                                        ) : (
                                          <Text
                                            key={field}
                                            fw={field === 'name' ? 700 : 300}
                                            c={field !== 'name' ? 'dimmed' : undefined}
                                            size={field === 'comment' || field === 'notes' ? 'sm' : 'md'}
                                          >
                                            {item[field]}
                                          </Text>
                                        )
                                      ) : null
                                    )}
                                  </div>
                                </Group>
                              </Card>
                            ))}
                          </Stack>
                        </Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>

                    <Space h="md" />
                  </div>
                );
              })}
            </Container>
          </Tabs.Panel>
        </Tabs>
      </div>
    </div>
  );
};

export default SiteOverviewPanel;
